import { type ReactElement, useCallback, useState } from 'react';
import { Alert, Box, Text } from 'components/ui';
import { SUMMARY_DASHBOARD_TEST_IDS } from '../../SummaryDashboard.constants';
import DashboardPromptInput from '../DashboardPromptInput';
import {
  DASHBOARD_PROMPT_BAR_EDIT_LABEL,
  DASHBOARD_PROMPT_BAR_EMPTY_LABEL,
  DASHBOARD_PROMPT_BAR_MAX_ROWS,
  DASHBOARD_PROMPT_BAR_SUBMIT_LABEL,
} from './DashboardPromptBar.constants';
import type { DashboardPromptBarProps } from './DashboardPromptBar.props';
import {
  collapsedBarSx,
  collapsedEditLabelSx,
  collapsedIconSx,
  collapsedPlaceholderTextSx,
  collapsedTextSx,
  promptBarErrorSx,
  promptBarWrapperSx,
} from './DashboardPromptBar.styles';
import {
  DASHBOARD_PROMPT_BUSY_HINT,
  DASHBOARD_PROMPT_PLACEHOLDER,
  DASHBOARD_PROMPT_SUBMIT_HINT,
} from '../DashboardPromptInput/DashboardPromptInput.constants';

const SCALAR_AI_ICON_SRC = '/images/scalar_ai.png';

const PROMPT_INPUT_TEST_IDS = {
  input: SUMMARY_DASHBOARD_TEST_IDS.promptInput,
  submitButton: SUMMARY_DASHBOARD_TEST_IDS.promptSubmitButton,
  submitHint: SUMMARY_DASHBOARD_TEST_IDS.promptSubmitHint,
};

// The prompt of a populated dashboard, pinned to the top of the page and collapsed to a single line.
// Interacting with it expands the full editor seeded with the stored prompt. Submitting revises the
// existing dashboard rather than rebuilding it, so the text may be either a corrected brief or a
// bare instruction ("delete gross IRR"); the stored prompt comes back as the brief with that edit
// folded in, never the instruction itself. See useDashboardGeneration.
//
// Reuses the same test ids as the empty state's input on purpose: only one of the two is ever
// mounted, since the bar exists only once a dashboard has components.
const DashboardPromptBar = ({
  errorMessage,
  isGenerating = false,
  onSubmit,
  prompt,
}: DashboardPromptBarProps): ReactElement => {
  const [isExpanded, setIsExpanded] = useState(false);

  const expand = useCallback(() => setIsExpanded(true), []);
  // Discards the edit: the input is unmounted, so the next expand re-seeds from the stored prompt.
  const collapse = useCallback(() => setIsExpanded(false), []);

  const handleSubmit = useCallback(
    async (nextPrompt: string): Promise<boolean> => {
      const didSucceed = await onSubmit(nextPrompt);
      if (didSucceed) {
        setIsExpanded(false);
      }
      return didSucceed;
    },
    [onSubmit]
  );

  return (
    <Box data-testid={SUMMARY_DASHBOARD_TEST_IDS.promptBar} sx={promptBarWrapperSx}>
      {/* `isGenerating` keeps the editor mounted even if Escape collapsed it mid-request, so the
          in-flight rebuild stays visible instead of silently finishing behind a collapsed bar. */}
      {isExpanded || isGenerating ? (
        <DashboardPromptInput
          autoFocus
          busyHint={DASHBOARD_PROMPT_BUSY_HINT}
          initialValue={prompt ?? ''}
          isBusy={isGenerating}
          maxRows={DASHBOARD_PROMPT_BAR_MAX_ROWS}
          onCancel={collapse}
          onSubmit={handleSubmit}
          placeholder={DASHBOARD_PROMPT_PLACEHOLDER}
          submitHint={DASHBOARD_PROMPT_SUBMIT_HINT}
          submitLabel={DASHBOARD_PROMPT_BAR_SUBMIT_LABEL}
          testIds={PROMPT_INPUT_TEST_IDS}
        />
      ) : (
        // A real button, so the row is reachable by keyboard and announces itself as actionable.
        <Box
          component="button"
          data-testid={SUMMARY_DASHBOARD_TEST_IDS.promptBarCollapsed}
          onClick={expand}
          onFocus={expand}
          sx={collapsedBarSx}
          type="button">
          <Box aria-hidden alt="" component="img" src={SCALAR_AI_ICON_SRC} sx={collapsedIconSx} />
          <Text component="span" sx={prompt ? collapsedTextSx : collapsedPlaceholderTextSx}>
            {prompt || DASHBOARD_PROMPT_BAR_EMPTY_LABEL}
          </Text>
          <Box component="span" sx={collapsedEditLabelSx}>
            {DASHBOARD_PROMPT_BAR_EDIT_LABEL}
          </Box>
        </Box>
      )}
      {Boolean(errorMessage) && (
        <Alert data-testid={SUMMARY_DASHBOARD_TEST_IDS.promptError} severity="error" sx={promptBarErrorSx}>
          {errorMessage}
        </Alert>
      )}
    </Box>
  );
};

export default DashboardPromptBar;
